import hljs from "highlight.js/lib/core";
import "highlight.js/styles/github.css";
import katexAutoRender from "katex/contrib/auto-render";
import { common } from "lowlight";
import MarkdownIt from "markdown-it";
import MarkdownInline from "markdown-it-for-inline";
import MarkdownItHighlightJs from "markdown-it-highlightjs/core";

import {
  MarkdownItCodeWrapOptions,
  markDownItCodeWrap,
} from "./MarkdownItCodeWrap";

Object.entries(common).forEach(([lang, value]) => {
  hljs.registerLanguage(lang, value);
});

export const highlightJs = hljs;

type Options = {
  markdownItCodeWrap: MarkdownItCodeWrapOptions;
};
export const setupMarkdownIt = (options: Options) => {
  const markdownIt = new MarkdownIt();
  markdownIt.use(MarkdownItHighlightJs, {
    hljs,
  });

  markdownIt.use(markDownItCodeWrap, options.markdownItCodeWrap);

  // add target="_blank" to all links
  markdownIt.use(
    MarkdownInline,
    "url_new_win",
    "link_open",
    // @ts-ignore no idea how to get the types
    function (tokens: MarkdownIt.Token[], idx: number) {
      tokens[idx].attrSet("target", "_blank");
    }
  );

  // use wrapper for horizontal scrolling
  // see https://getbootstrap.com/docs/5.3/content/tables/#responsive-tables
  markdownIt.renderer.rules.table_open = function () {
    return `<div class='table-responsive'><table class='table'>`;
  };
  markdownIt.renderer.rules.table_close = function () {
    return `</table></div>`;
  };

  return markdownIt;
};

// replace \[ with $$ and \] with $$
// \( and \) with $
export const preProcessMath = (markdownText: string) => {
  // First replace LaTeX delimiters
  let processedText = markdownText
    .replace(/\\\[/g, "$$$") // replace all occurrences of \[ with $$
    .replace(/\\\]/g, "$$$") // replace all occurrences of \] with $$
    .replace(/\\\(/g, "$$") // replace all occurrences of \( with $
    .replace(/\\\)/g, "$$"); // replace all occurrences of \) with $

  // Fix LaTeX matrix row separators
  // --------------------------------------------
  // ISSUE: In LaTeX, matrices use double backslashes (\\) to separate rows.
  // When these pass through markdown processing, they aren't properly interpreted,
  // causing matrices to render as a single line instead of in a grid format.
  //
  // SOLUTION: Replace double backslashes with \newline which KaTeX correctly
  // interprets as a line break in mathematical environments.
  //
  // IMPLEMENTATION DETAILS:
  // - The regex /\\\\(?!\\\s*$)/gm matches double backslashes (\\)
  // - The negative lookahead (?!\\\s*$) ensures we don't match backslashes at the end of a line
  // - This prevents accidental replacement of other LaTeX commands that use backslashes
  processedText = processedText.replace(/\\\\(?!\\\s*$)/gm, "\\newline");

  return processedText;
};

export const renderKatex = (el: HTMLElement) => {
  katexAutoRender(el, {
    delimiters: [
      { left: "$$", right: "$$", display: true },
      { left: "$", right: "$", display: false },
      { left: "\\(", right: "\\)", display: false },
      { left: "\\[", right: "\\]", display: true },
      { left: "\\begin{equation}", right: "\\end{equation}", display: true },
      { left: "\\begin{align}", right: "\\end{align}", display: true },
      { left: "\\begin{alignat}", right: "\\end{alignat}", display: true },
      { left: "\\begin{gather}", right: "\\end{gather}", display: true },
      { left: "\\begin{CD}", right: "\\end{CD}", display: true },
    ],
    throwOnError: false,
  });
};
